import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';

// Generic searchable dropdown inspired by CompanyAutocomplete
// Props:
// - options: [{ id, label }]
// - value: { id, label } | null
// - onChange: fn(option|null)
// - placeholder: string
// - disabled: bool
// - error: string|null
// - loading: bool
//
// A lista é renderizada num PORTAL, com posição fixa calculada a partir do campo. Isso resolve dois
// problemas que apareciam sempre que o componente vivia dentro de um modal (`ModalMeta`,
// `ModalCriarContaPagar`): posicionada em `absolute`, ela era recortada pelo `overflow-y-auto` do
// modal e, mesmo fora do fluxo, ainda contava para a área rolável dele — abrir o seletor fazia
// nascer uma barra de rolagem no modal inteiro.
// Fora da assinatura do componente DE PROPÓSITO. Como valor padrão literal (`= ['label', 'id']`)
// ele era um array novo a cada render; é dependência do efeito que filtra as opções, e esse efeito
// grava estado — sem a prop, o seletor re-renderizava para sempre.
const CAMPOS_DE_BUSCA_PADRAO = ['label', 'id'];

const MARGEM = 8;
const LARGURA_MAXIMA = 448;
const ALTURA_MINIMA = 160;

export default function SearchableSelect({
  options = [],
  value = null,
  onChange,
  placeholder = 'Selecione...',
  disabled = false,
  error = null,
  loading = false,
  searchFields = CAMPOS_DE_BUSCA_PADRAO,
  className = '',
  inputClassName = '',
  dropdownClassName = '',
  // Nome acessível do campo, para quando o rótulo visível não é um <label> ligado a ele.
  ariaLabel,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [posicao, setPosicao] = useState(null);
  const listboxId = useId();
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const toLower = (v) => String(v ?? '').toLowerCase();
  const onlyDigits = (v) => String(v ?? '').replace(/\D+/g, '');

  useEffect(() => {
    const selectedLabel = value && typeof value === 'object' ? (value.label || '') : '';
    if (!searchTerm || (selectedLabel && searchTerm === selectedLabel)) {
      setFiltered(options.slice(0, 100)); // safety limit
      return;
    }
    const term = toLower(searchTerm.trim());
    const termDigits = onlyDigits(searchTerm);
    const fields = Array.isArray(searchFields) && searchFields.length ? searchFields : ['label','id'];

    const matches = (o) => {
      for (const key of fields) {
        const val = o[key];
        if (val == null) continue;
        const valStr = toLower(val);
        if (valStr.includes(term)) return true;
        if (key === 'cpf_cnpj') {
          if (onlyDigits(val).includes(termDigits) && termDigits.length >= 3) return true;
        }
      }
      // Always allow match by id numeric string
      if (String(o.id ?? '').toLowerCase().includes(term)) return true;
      return false;
    };

    setFiltered(options.filter(matches).slice(0, 200));
  }, [searchTerm, options, searchFields, value]);

  useEffect(() => {
    if (value && typeof value === 'object') {
      setSearchTerm(value.label || '');
    } else if (!value) {
      setSearchTerm('');
    }
  }, [value]);

  // Mede o campo e decide se a lista abre para baixo ou para cima. `minWidth` acompanha o campo
  // para a lista nunca ficar mais estreita que ele; a largura pode crescer além disso, porque o
  // campo costuma ser uma coluna estreita de grid e o nome inteiro não caberia.
  const atualizarPosicao = useCallback(() => {
    const alvo = triggerRef.current;
    if (!alvo) return;

    const r = alvo.getBoundingClientRect();
    const espacoAbaixo = window.innerHeight - r.bottom - MARGEM;
    const espacoAcima = r.top - MARGEM;
    const abrirAcima = espacoAbaixo < ALTURA_MINIMA && espacoAcima > espacoAbaixo;

    setPosicao({
      left: r.left,
      top: abrirAcima ? undefined : r.bottom + MARGEM,
      bottom: abrirAcima ? window.innerHeight - r.top + MARGEM : undefined,
      minWidth: r.width,
      maxWidth: Math.max(r.width, Math.min(LARGURA_MAXIMA, window.innerWidth - r.left - MARGEM)),
      maxHeight: Math.max(ALTURA_MINIMA, (abrirAcima ? espacoAcima : espacoAbaixo) - MARGEM),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosicao(null);
      return undefined;
    }

    atualizarPosicao();

    // `true` na captura: pega a rolagem de QUALQUER ancestral, inclusive a do próprio modal, e não
    // só a da janela. Sem isso a lista fica parada enquanto o conteúdo atrás dela rola.
    const aoMover = () => atualizarPosicao();
    window.addEventListener('scroll', aoMover, true);
    window.addEventListener('resize', aoMover);

    return () => {
      window.removeEventListener('scroll', aoMover, true);
      window.removeEventListener('resize', aoMover);
    };
  }, [isOpen, atualizarPosicao]);

  useEffect(() => {
    // A lista mora num portal, fora da árvore do campo, então o clique fora precisa checar os DOIS
    // nós. Checar só o campo fecharia o seletor ao clicar numa opção.
    const handleClickOutside = (event) => {
      const dentroDoCampo = triggerRef.current?.contains(event.target);
      const dentroDaLista = listRef.current?.contains(event.target);
      if (!dentroDoCampo && !dentroDaLista) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClear = () => {
    onChange?.(null);
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const lista = isOpen && posicao ? createPortal(
    <div
      ref={listRef}
      id={listboxId}
      role="listbox"
      style={{
        position: 'fixed',
        left: posicao.left,
        top: posicao.top,
        bottom: posicao.bottom,
        minWidth: posicao.minWidth,
        maxWidth: posicao.maxWidth,
        maxHeight: posicao.maxHeight,
        width: 'max-content',
      }}
      className={`z-[60] overflow-y-auto rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-1.5 shadow-[0_18px_50px_rgba(2,6,23,0.28)] backdrop-blur-xl ${dropdownClassName}`}
    >
      {loading ? (
        <div className="p-3 text-center text-sm text-[color:var(--text-muted)]">Carregando...</div>
      ) : filtered.length > 0 ? (
        filtered.map((opt) => {
          const selected = String(value?.id ?? '') === String(opt.id);

          return (
            <button
              key={opt.id}
              role="option"
              aria-selected={selected}
              type="button"
              title={opt.label}
              onClick={() => { onChange?.(opt); setIsOpen(false); setSearchTerm(opt.label || ''); }}
              className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                selected
                  ? 'bg-[color:var(--accent)] text-white shadow-[0_8px_24px_rgba(114,19,173,0.22)]'
                  : 'text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]'
              }`}
            >
              {/* Sem `truncate`: o nome do vendedor é a informação que se veio buscar aqui, e
                  cortá-lo com reticências obrigava a abrir a opção para descobrir quem é. */}
              <span className="min-w-0 flex-1 break-words">{opt.label}</span>
              {selected && <Check className="mt-0.5 h-4 w-4 shrink-0" />}
            </button>
          );
        })
      ) : (
        <div className="p-3 text-center text-sm text-[color:var(--text-muted)]">Sem resultados</div>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className={`relative ${className}`} ref={triggerRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-muted)]" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          value={searchTerm}
          // O campo é estreito por natureza — costuma ser uma coluna de grid —, então o nome
          // selecionado quase sempre fica cortado. O `title` deixa o valor inteiro a um hover.
          title={searchTerm || undefined}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`h-11 w-full rounded-xl border bg-[color:var(--surface)] py-2.5 pl-10 pr-20 text-sm font-medium text-[color:var(--text-primary)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] outline-none transition placeholder:text-[color:var(--text-muted)] hover:border-[color:var(--accent)] focus:border-[color:var(--accent)] focus:ring-2 focus:ring-[color:var(--accent)] ${
            error ? 'border-red-300' : 'border-[color:var(--border)]'
          } ${disabled ? 'cursor-not-allowed bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]' : ''} ${inputClassName}`}
        />
        <div className="absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded-full p-1 transition hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
              aria-label="Limpar seleção"
            >
              <X className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="rounded-full p-1 transition hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
            aria-label={isOpen ? 'Fechar opções' : 'Abrir opções'}
          >
            <ChevronDown className={`h-4 w-4 text-[color:var(--text-muted)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      {lista}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
